import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";
import useFetchBlogBySlug from "../../Hooks/useFetchBlogBySlug";
import useGetComments from "../../Hooks/useGetComments";
import useCreateComment from "../../Hooks/useCreateComment";
import useFetchBlogs from "../../Hooks/useFetchBlogs";
import { useState } from "react";
import "./BlogDetail.css";
import { BlogDetailSkeleton, CommentSkeleton, RelatedBlogSkeleton } from "../../Components/BlogSkeleton";

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "January 1, 2025";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (e) {
    return "January 1, 2025";
  }
};

// Helper function to calculate read time
const calculateReadTime = (content) => {
  if (!content) return "3 min read";
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  const readTime = Math.ceil(wordCount / wordsPerMinute);
  return `${readTime} min read`;
};

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { blog, isLoading, isError, error, fetchBlogBySlug } = useFetchBlogBySlug();
  const { comments, isLoading: commentsLoading, refetch: refetchComments } = useGetComments(
    blog?._id
  );
  const { createComment, isLoading: isSubmitting } = useCreateComment();
  const { blogs: allBlogs, isLoading: blogsLoading } = useFetchBlogs();
  
  const [commentForm, setCommentForm] = useState({
    content: "",
    nickname: "",
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Get related blogs (exclude current blog)
  const relatedBlogs = allBlogs
    .filter((b) => b._id !== blog?._id && b.slug)
    .slice(0, 5)
    .map((b) => ({
      id: b._id,
      title: b.title,
      slug: b.slug,
      excerpt: b.excerpt || b.content?.substring(0, 100) + "..." || "",
      coverImage: b.coverImage,
      date: formatDate(b.createdAt),
    }));

  useEffect(() => {
    if (slug) {
      fetchBlogBySlug(slug);
    }
  }, [slug, fetchBlogBySlug]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!blog?._id || !commentForm.content || !commentForm.nickname) return;

    try {
      await createComment(blog._id, commentForm);
      setCommentForm({ content: "", nickname: "" });
      setShowSuccessMessage(true);
      refetchComments();
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
    } catch (err) {
      console.error("Failed to submit comment:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="blog-detail-container">
        <div className="blog-detail-wrapper">
          <div className="blog-detail-main">
            <BlogDetailSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !blog) {
    return (
      <div className="blog-detail-container">
        <div className="blog-detail-content">
          <button onClick={() => navigate("/news")} className="back-button">
            <BsArrowLeft /> Back to News
          </button>
          <div className="error-message">
            {error?.message || "Blog not found"}
          </div>
        </div>
      </div>
    );
  }

  const approvedComments = comments.filter((comment) => comment.isApproved);

  return (
    <div className="blog-detail-container">
      <div className="blog-detail-wrapper">
        <div className="blog-detail-main">
          <button onClick={() => navigate("/news")} className="back-button">
            <BsArrowLeft /> Back to News
          </button>

          <article className="blog-article">
            <header className="blog-header">
              <h1 className="blog-title">{blog.title || "Untitled"}</h1>
              <div className="blog-meta">
                <span className="blog-date">{formatDate(blog.createdAt)}</span>
                <span className="blog-read-time">
                  {calculateReadTime(blog.content)}
                </span>
              </div>
            </header>

            {(blog.coverImage || blog.image) && (
              <div className="blog-cover-image">
                <img 
                  src={blog.coverImage || blog.image} 
                  alt={blog.title || "Blog image"} 
                  loading="lazy"
                />
              </div>
            )}

            <div className="blog-content">
              {blog.excerpt && (
                <p className="blog-excerpt">{blog.excerpt}</p>
              )}
              <div className="blog-body">{blog.content}</div>
            </div>
          </article>

          <section className="comments-section">
            <h2 className="comments-title">Comments ({approvedComments.length})</h2>

            {commentsLoading ? (
              <div className="comments-list">
                {[...Array(3)].map((_, index) => (
                  <CommentSkeleton key={index} />
                ))}
              </div>
            ) : approvedComments.length > 0 ? (
              <div className="comments-list">
                {approvedComments.map((comment) => (
                  <div key={comment._id} className="comment-item">
                    <div className="comment-header">
                      <span className="comment-nickname">{comment.nickname}</span>
                      <span className="comment-date">
                        {formatDate(comment.createdAt)}
                      </span>
                    </div>
                    <p className="comment-content">{comment.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-comments">No comments yet. Be the first to comment!</p>
            )}

            <form onSubmit={handleSubmitComment} className="comment-form">
              <h3>Add a Comment</h3>
              {showSuccessMessage && (
                <div className="success-message">
                  ✓ Your comment has been submitted successfully!
                </div>
              )}
              <div className="form-group">
                <label htmlFor="nickname">Nickname</label>
                <input
                  type="text"
                  id="nickname"
                  value={commentForm.nickname}
                  onChange={(e) =>
                    setCommentForm({ ...commentForm, nickname: e.target.value })
                  }
                  required
                  placeholder="Your nickname"
                />
              </div>
              <div className="form-group">
                <label htmlFor="content">Comment</label>
                <textarea
                  id="content"
                  value={commentForm.content}
                  onChange={(e) =>
                    setCommentForm({ ...commentForm, content: e.target.value })
                  }
                  required
                  rows="4"
                  placeholder="Write your comment here..."
                />
              </div>
              <button type="submit" disabled={isSubmitting} className="submit-button">
                {isSubmitting ? "Submitting..." : "Submit Comment"}
              </button>
            </form>
          </section>
        </div>

        <aside className="blog-sidebar">
          <div className="sidebar-content">
            <h3 className="sidebar-title">Latest News</h3>
            {blogsLoading ? (
              <div className="related-blogs">
                {[...Array(5)].map((_, index) => (
                  <RelatedBlogSkeleton key={index} />
                ))}
              </div>
            ) : relatedBlogs.length > 0 ? (
              <div className="related-blogs">
                {relatedBlogs.map((relatedBlog) => (
                  <Link
                    key={relatedBlog.id}
                    to={`/news/${relatedBlog.slug}`}
                    className="related-blog-card"
                  >
                    {relatedBlog.coverImage && (
                      <div className="related-blog-image">
                        <img 
                          src={relatedBlog.coverImage} 
                          alt={relatedBlog.title} 
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="related-blog-info">
                      <span className="related-blog-date">{relatedBlog.date}</span>
                      <h4 className="related-blog-title">{relatedBlog.title}</h4>
                      {relatedBlog.excerpt && (
                        <p className="related-blog-excerpt">{relatedBlog.excerpt}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="no-related">No related blogs available</p>
            )}
            
            <div className="sidebar-cta">
              <Link to="/news" className="view-all-link">
                View All News <BsArrowRight />
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default BlogDetail;

